import { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-location';
import useApi from 'utils/use-api';
import Modal from 'components/layouts/modal';
import TextInput from 'components/core/text-input';
import { ShieldCheckIcon, ShieldExclamationIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useAuthStore, setMe } from 'state/auth';

const TwoFactorAuthPage: FC = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentUser = useAuthStore(state => state.user);

  const [enableModal, setEnableModal] = useState(false);
  const [regenerateModal, setRegenerateModal] = useState(false);
  const [setupData, setSetupData] = useState<{ qrCodeDataURL: string; base32: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [showRecoveryCodesModal, setShowRecoveryCodesModal] = useState(false);

  useEffect(() => {
    document.title = 'Two-Factor Authentication';
  }, []);

  // Fetch 2FA status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: () => api.get('/auth/2fa/status').then(res => res.data),
  });

  const enableMutation = useMutation({
    mutationFn: () => api.post('/auth/2fa/enable').then(res => res.data),
    onSuccess: data => {
      setSetupData(data);
      setEnableModal(true);
    },
    onError: (error: any) => {
      toast(JSON.stringify({ type: 'error', title: 'Error', description: error?.response?.data?.message || 'Failed to enable 2FA' }));
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (token: string) => api.post('/auth/2fa/confirm', { token }).then(res => res.data),
    onSuccess: data => {
      toast(JSON.stringify({ type: 'success', title: 'Success', description: '2FA has been enabled successfully!' }));
      setEnableModal(false);
      setSetupData(null);
      if (data.recoveryCodes) {
        setRecoveryCodes(data.recoveryCodes);
        setShowRecoveryCodesModal(true);
      }
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      confirmForm.resetForm();
    },
    onError: (error: any) => {
      toast(JSON.stringify({ type: 'error', title: 'Error', description: error?.response?.data?.message || 'Invalid verification code' }));
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (password: string) => api.post('/auth/2fa/regenerate-codes', { password }).then(res => res.data),
    onSuccess: data => {
      toast(JSON.stringify({ type: 'success', title: 'Success', description: 'New recovery codes generated!' }));
      setRegenerateModal(false);
      if (data.recoveryCodes) {
        setRecoveryCodes(data.recoveryCodes);
        setShowRecoveryCodesModal(true);
      }
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      regenerateForm.resetForm();
    },
    onError: (error: any) => {
      toast(JSON.stringify({ type: 'error', title: 'Error', description: error?.response?.data?.message || 'Failed to regenerate codes' }));
    },
  });

  const confirmForm = useFormik({
    initialValues: { token: '' },
    validationSchema: Yup.object().shape({
      token: Yup.string().required('Verification code is required').min(6, 'Code must be at least 6 characters'),
    }),
    onSubmit: values => {
      confirmMutation.mutate(values.token);
    },
  });

  const regenerateForm = useFormik({
    initialValues: { password: '' },
    validationSchema: Yup.object().shape({
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: values => {
      regenerateMutation.mutate(values.password);
    },
  });

  const handleCopyRecoveryCodes = () => {
    if (recoveryCodes) {
      navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast(JSON.stringify({ type: 'success', title: 'Copied', description: 'Recovery codes copied to clipboard' }));
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Two-Factor Authentication</h3>
            <p className="mt-1 text-sm text-gray-500">Two-factor authentication is required for all accounts</p>
          </div>

          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start space-x-4">
              <div className={`flex-shrink-0 rounded-full p-3 ${status?.enabled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {status?.enabled ? (
                  <ShieldCheckIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <ShieldExclamationIcon className="h-6 w-6 text-yellow-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {status?.enabled ? 'Two-Factor Authentication is Active' : 'Two-Factor Authentication Required'}
                </p>
                <p className="text-sm text-gray-500">
                  {status?.enabled
                    ? 'Your account is protected with an authenticator app.'
                    : 'Please set up two-factor authentication to secure your account.'}
                </p>
                {status?.enabled && status?.remainingCodes !== undefined && (
                  <p className={`text-sm mt-1 ${status.remainingCodes <= 2 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    <KeyIcon className="h-4 w-4 inline mr-1" />
                    {status.remainingCodes} recovery codes remaining
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 flex flex-col space-y-2">
                {status?.enabled ? (
                  <button
                    type="button"
                    onClick={() => setRegenerateModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Regenerate Codes
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => enableMutation.mutate()}
                    disabled={enableMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {enableMutation.isPending ? 'Setting up...' : 'Set Up 2FA'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800">How it works</h4>
          <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
            <li>Install an authenticator app like Google Authenticator, Authy, or 1Password</li>
            <li>Scan the QR code with your authenticator app</li>
            <li>Enter the 6-digit code from your app to verify</li>
            <li>Save your recovery codes in a secure location</li>
          </ul>
        </div>
      </div>

      <Modal
        open={enableModal}
        setOpen={setEnableModal}
        title="Set Up Two-Factor Authentication"
        size="4xl"
        renderActions={() => (
          <button
            type="button"
            onClick={() => confirmForm.handleSubmit()}
            disabled={confirmMutation.isPending}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {confirmMutation.isPending ? 'Verifying...' : 'Verify & Enable'}
          </button>
        )}
      >
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-4">Scan this QR code with your authenticator app</p>
            {setupData?.qrCodeDataURL && (
              <div className="flex justify-center mb-4">
                <div className="bg-white p-4 rounded-lg shadow-inner">
                  <img src={setupData.qrCodeDataURL} alt="2FA QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}
            <div className="bg-gray-100 rounded-md p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Or enter this code manually:</p>
              <p className="font-mono text-sm break-all">{setupData?.base32}</p>
            </div>
          </div>
          <form onSubmit={confirmForm.handleSubmit}>
            <TextInput id="token" label="Enter verification code" placeholder="000000" {...confirmForm} />
          </form>
        </div>
      </Modal>

      <Modal
        open={regenerateModal}
        setOpen={setRegenerateModal}
        title="Regenerate Recovery Codes"
        size="3xl"
        renderActions={() => (
          <button
            type="button"
            onClick={() => regenerateForm.handleSubmit()}
            disabled={regenerateMutation.isPending}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {regenerateMutation.isPending ? 'Generating...' : 'Regenerate Codes'}
          </button>
        )}
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-800">
              Warning: Regenerating recovery codes will invalidate all your existing recovery codes. Make sure to save the new ones.
            </p>
          </div>
          <form onSubmit={regenerateForm.handleSubmit}>
            <TextInput id="password" label="Enter your password to confirm" type="password" {...regenerateForm} />
          </form>
        </div>
      </Modal>

      <Modal
        open={showRecoveryCodesModal}
        setOpen={val => {
          setShowRecoveryCodesModal(val);
          if (!val) {
            if (currentUser) {
              setMe({ ...currentUser, twoFAEnabled: true });
            }
            navigate({ to: '/', replace: true });
          }
        }}
        title="Your Recovery Codes"
        size="4xl"
        renderActions={() => (
          <>
            <button
              type="button"
              onClick={handleCopyRecoveryCodes}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Copy Codes
            </button>
            <button
              type="button"
              onClick={() => {
                setRecoveryCodes(null);
                setShowRecoveryCodesModal(false);
              }}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              I've Saved My Codes
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800 font-medium">
              IMPORTANT: Save these codes in a secure location. They can be used to access your account if you lose access to your authenticator app.
              Each code can only be used once.
            </p>
          </div>
          <div className="bg-gray-100 rounded-md p-4 font-mono text-sm">
            <div className="grid grid-cols-2 gap-2">
              {recoveryCodes?.map((code, index) => (
                <div key={code} className="bg-white px-3 py-2 rounded border border-gray-200">
                  {index + 1}. {code}
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center">These codes have also been sent to your email address: {currentUser?.email}</p>
        </div>
      </Modal>
    </div>
  );
};

export default TwoFactorAuthPage;
